import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';

import { inputMint, outputMint, TOKEN_DECIMALS } from '@/constants/solana';

const RAYDIUM_API_URLS = {
  BASE_HOST: 'https://api-v3.raydium.io',
  SWAP_HOST: 'https://transaction-v1.raydium.io',
  PRIORITY_FEE: '/main/auto-fee',
} as const;

export interface SwapQuote {
  success: boolean;
  data: {
    outputAmount: string;
    otherAmountThreshold?: string;
  };
}

export const fetchPriorityFee = async () => {
  const response = await fetch(
    `${RAYDIUM_API_URLS.BASE_HOST}${RAYDIUM_API_URLS.PRIORITY_FEE}`,
  );
  return (await response.json()) as {
    data: { default: { h: number } };
  };
};

export const fetchQuote = async (amount: number) => {
  let slippage = 50;
  if (amount > 1000) {
    slippage = 100;
  }
  try {
    const response = await fetch(
      `${
        RAYDIUM_API_URLS.SWAP_HOST
      }/compute/swap-base-in?inputMint=${inputMint}&outputMint=${outputMint}&amount=${Math.ceil(amount * LAMPORTS_PER_SOL)}&slippageBps=${
        slippage
      }&txVersion=${'LEGACY'}`,
    );

    if (!response.ok) {
      throw new Error(`Error fetching quote: ${response.status}`);
    }

    return (await response.json()) as SwapQuote; // Parse the JSON response
  } catch (error) {
    console.error('Failed to fetch quote:', error);
    throw error;
  }
};

export const fetchQuoteSol = async (amount: number) => {
  let slippage = 50;
  if (amount > 200000) {
    slippage = 100;
  }
  try {
    const response = await fetch(
      `${
        RAYDIUM_API_URLS.SWAP_HOST
      }/compute/swap-base-in?inputMint=${outputMint}&outputMint=${inputMint}&amount=${amount * TOKEN_DECIMALS}&slippageBps=${
        slippage
      }&txVersion=${'LEGACY'}`,
    );

    if (!response.ok) {
      throw new Error(`Error fetching quote: ${response.status}`);
    }

    return (await response.json()) as SwapQuote; // Parse the JSON response
  } catch (error) {
    console.error('Failed to fetch quote:', error);
    throw error;
  }
};

//** Sol to USDC*/
export const useFetchQuote = (amount: string) => {
  const [debouncedAmount] = useDebounce(amount, 1000);
  return useQuery({
    queryKey: ['quote', debouncedAmount],
    enabled:
      !!debouncedAmount && +debouncedAmount > 0 && !isNaN(+debouncedAmount),
    refetchInterval: 10000,
    queryFn: async () => {
      return await fetchQuote(+debouncedAmount);
    },
  });
};

//** USDC to SOL*/
export const useFetchQuoteSol = (amount: string) => {
  const [debouncedAmount] = useDebounce(amount, 1000);
  return useQuery({
    queryKey: ['quoteSol', debouncedAmount],
    enabled:
      !!debouncedAmount && +debouncedAmount > 0 && !isNaN(+debouncedAmount),
    refetchInterval: 10000,
    queryFn: async () => {
      return await fetchQuoteSol(+debouncedAmount);
    },
  });
};

export const useFetchApproxExchangeRate = () => {
  return useQuery({
    queryKey: ['approxExchangeRate'],
    refetchInterval: 10000,
    queryFn: async () => {
      const solToUsdcQuote = await fetchQuote(1);
      const usdcToSolQuote = await fetchQuoteSol(100);

      const usdcPerSol =
        Number(solToUsdcQuote?.data?.outputAmount || 0) / TOKEN_DECIMALS;
      const usdcPerSolMinimum =
        Number(solToUsdcQuote?.data?.otherAmountThreshold || 0) /
        TOKEN_DECIMALS;

      const solPerUsdc =
        (Number(usdcToSolQuote?.data?.outputAmount || 0) /
          100 /
          LAMPORTS_PER_SOL) *
        1.01;
      const solPerUsdcMinimum =
        (Number(usdcToSolQuote?.data?.otherAmountThreshold || 0) /
          100 /
          LAMPORTS_PER_SOL) *
        1.01;

      return {
        usdcPerSol,
        usdcPerSolMinimum,
        solPerUsdc,
        solPerUsdcMinimum,
      };
    },
  });
};
